import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { ProcessedFile } from "@/components/InvoiceList";
import type { FailedFile } from "@/components/FailedFileSplitView";

interface PendingFile {
  id: string;
  file: File;
}

/** Serialisable metadata stored in jsonb (no File blobs) */
interface PendingFileMeta {
  id: string;
  storagePath: string;
  name: string;
  type: string;
  size: number;
  lastModified: number;
}

interface ProcessedFileMeta extends Omit<ProcessedFile, "file"> {
  storagePath: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileLastModified: number;
}

interface FailedFileMeta extends Omit<FailedFile, "file"> {
  storagePath: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileLastModified: number;
}

export type TrashedFileOrigin = "processed" | "failed";

export interface TrashedFile {
  file: ProcessedFile | FailedFile;
  origin: TrashedFileOrigin;
}

interface TrashedFileMeta {
  origin: TrashedFileOrigin;
  fileName: string;
  fileType: string;
  fileSize: number;
  fileLastModified: number;
  storagePath: string;
  /** Serialised file payload minus the File object. */
  data: Omit<ProcessedFile, "file"> | Omit<FailedFile, "file">;
}

// ─── Storage helpers ───────────────────────────────────────

const BUCKET = "invoice-files";

const uploadFile = async (userId: string, fileId: string, file: File): Promise<string> => {
  const ext = file.name.split(".").pop() || "bin";
  const path = `${userId}/${fileId}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: true });
  if (error) throw error;
  return path;
};

const downloadFile = async (path: string, fileName: string, fileType: string): Promise<File> => {
  const { data, error } = await supabase.storage.from(BUCKET).download(path);
  if (error) throw error;
  return new File([data], fileName, { type: fileType });
};

const deleteFolder = async (userId: string) => {
  // List all files in user folder and delete them
  const { data } = await supabase.storage.from(BUCKET).list(userId, { limit: 1000 });
  if (data && data.length > 0) {
    const paths = data.map((f) => `${userId}/${f.name}`);
    await supabase.storage.from(BUCKET).remove(paths);
  }
};

// ─── Serialisation ─────────────────────────────────────────

const serialisePending = (files: PendingFile[], pathMap: Map<string, string>): PendingFileMeta[] =>
  files.map((pf) => ({
    id: pf.id,
    storagePath: pathMap.get(pf.id) || "",
    name: pf.file.name,
    type: pf.file.type,
    size: pf.file.size,
    lastModified: pf.file.lastModified,
  }));

const serialiseProcessed = (files: ProcessedFile[], pathMap: Map<string, string>): ProcessedFileMeta[] =>
  files.map(({ file, attachments: _att, ...rest }) => ({
    ...rest,
    storagePath: pathMap.get(rest.id) || "",
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    fileLastModified: file.lastModified,
  }));

const serialiseFailed = (files: FailedFile[], pathMap: Map<string, string>): FailedFileMeta[] =>
  files.map(({ file, ...rest }) => ({
    ...rest,
    storagePath: pathMap.get(rest.id) || "",
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    fileLastModified: file.lastModified,
  }));

const serialiseTrashed = (files: TrashedFile[], pathMap: Map<string, string>): TrashedFileMeta[] =>
  files.map(({ file, origin }) => {
    const { file: f, ...rest } = file;
    return {
      origin,
      storagePath: pathMap.get(file.id) || "",
      fileName: f.name,
      fileType: f.type,
      fileSize: f.size,
      fileLastModified: f.lastModified,
      data: rest,
    };
  });

// ─── Hook ──────────────────────────────────────────────────

export interface InvoiceSessionState {
  pendingFiles: PendingFile[];
  processed: ProcessedFile[];
  failedFiles: FailedFile[];
  trashedFiles: TrashedFile[];
  prefix: string;
  startNumber: number;
  appliedCompanyId?: string | null;
}

export type SaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

export function useInvoiceSession(userId: string | undefined) {
  const [loading, setLoading] = useState(true);
  const [initialState, setInitialState] = useState<InvoiceSessionState | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  // Track uploaded paths: fileId → storagePath
  const pathMapRef = useRef<Map<string, string>>(new Map());
  // Track which file IDs are already uploaded to avoid duplicate uploads
  const uploadedIdsRef = useRef<Set<string>>(new Set());
  // Debounce timer for saving
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest state snapshot for saving
  const latestStateRef = useRef<InvoiceSessionState | null>(null);

  // ─── Load session ──────────────────────────────────────

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setInitialState(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const { data } = await supabase
          .from("invoice_sessions")
          .select("*")
          .eq("user_id", userId)
          .eq("environment", "received")
          .maybeSingle();


        if (cancelled) return;
        if (!data) {
          setInitialState({
            pendingFiles: [],
            processed: [],
            failedFiles: [],
            trashedFiles: [],
            prefix: "",
            startNumber: 1,
            appliedCompanyId: null,
          });
          setLoading(false);
          return;
        }

        const pendingMeta = (data.pending_files as unknown as PendingFileMeta[]) || [];
        const processedMeta = (data.processed_files as unknown as ProcessedFileMeta[]) || [];
        const failedMeta = (data.failed_files as unknown as FailedFileMeta[]) || [];
        const trashedMeta = (data.trashed_files as unknown as TrashedFileMeta[]) || [];

        // Download all files in parallel
        const downloadResults = await Promise.allSettled([
          ...pendingMeta.map(async (m) => {
            if (!m.storagePath) return null;
            const file = await downloadFile(m.storagePath, m.name, m.type);
            return { id: m.id, file, storagePath: m.storagePath, type: "pending" as const };
          }),
          ...processedMeta.map(async (m) => {
            if (!m.storagePath) return null;
            const file = await downloadFile(m.storagePath, m.fileName, m.fileType);
            return { id: m.id, file, storagePath: m.storagePath, type: "processed" as const };
          }),
          ...failedMeta.map(async (m) => {
            if (!m.storagePath) return null;
            const file = await downloadFile(m.storagePath, m.fileName, m.fileType);
            return { id: m.id, file, storagePath: m.storagePath, type: "failed" as const };
          }),
          ...trashedMeta.map(async (m) => {
            if (!m.storagePath) return null;
            const file = await downloadFile(m.storagePath, m.fileName, m.fileType);
            return { id: (m.data as { id: string }).id, file, storagePath: m.storagePath, type: "trashed" as const, origin: m.origin };
          }),
        ]);

        if (cancelled) return;

        const fileMap = new Map<string, File>();
        for (const r of downloadResults) {
          if (r.status === "fulfilled" && r.value) {
            fileMap.set(r.value.id, r.value.file);
            pathMapRef.current.set(r.value.id, r.value.storagePath);
            uploadedIdsRef.current.add(r.value.id);
          }
        }

        const pendingFiles: PendingFile[] = pendingMeta
          .filter((m) => fileMap.has(m.id))
          .map((m) => ({ id: m.id, file: fileMap.get(m.id)! }));

        const processed: ProcessedFile[] = processedMeta
          .filter((m) => fileMap.has(m.id))
          .map((m) => {
            const { storagePath, fileName, fileType, fileSize, fileLastModified, ...rest } = m;
            return { ...rest, file: fileMap.get(m.id)! } as ProcessedFile;
          });

        const failedFiles: FailedFile[] = failedMeta
          .filter((m) => fileMap.has(m.id))
          .map((m) => {
            const { storagePath, fileName, fileType, fileSize, fileLastModified, ...rest } = m;
            return { ...rest, file: fileMap.get(m.id)! } as FailedFile;
          });

        const trashedFiles: TrashedFile[] = trashedMeta
          .filter((m) => fileMap.has((m.data as { id: string }).id))
          .map((m) => {
            const { data, ...meta } = m;
            const { storagePath, fileName, fileType, fileSize, fileLastModified, ...rest } = data as any;
            const file = fileMap.get((data as { id: string }).id)!;
            const base = { ...rest, file } as ProcessedFile | FailedFile;
            return { file: base, origin: m.origin };
          });

        setInitialState({
          pendingFiles,
          processed,
          failedFiles,
          trashedFiles,
          prefix: data.prefix || "",
          startNumber: data.start_number || 1,
          appliedCompanyId: (data as { applied_company_id?: string | null }).applied_company_id ?? null,
        });
      } catch (e) {
        console.error("Failed to load invoice session:", e);
        setInitialState({
          pendingFiles: [],
          processed: [],
          failedFiles: [],
          trashedFiles: [],
          prefix: "",
          startNumber: 1,
          appliedCompanyId: null,
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  // ─── Save session (debounced) ──────────────────────────

  const saveSession = useCallback(
    async (state: InvoiceSessionState) => {
      if (!userId) return;

      latestStateRef.current = state;
      setSaveStatus("pending");

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      saveTimerRef.current = setTimeout(async () => {
        const s = latestStateRef.current;
        if (!s) return;

        setSaveStatus("saving");
        try {
          // Upload any new files that haven't been uploaded yet
          const allFiles: { id: string; file: File }[] = [
            ...s.pendingFiles.map((pf) => ({ id: pf.id, file: pf.file })),
            ...s.processed.map((pf) => ({ id: pf.id, file: pf.file })),
            ...s.failedFiles.map((ff) => ({ id: ff.id, file: ff.file })),
            ...s.trashedFiles.map((tf) => ({ id: tf.file.id, file: tf.file.file })),
          ];

          const pendingUploads = allFiles.filter((f) => !uploadedIdsRef.current.has(f.id));
          if (pendingUploads.length > 0) {
            // Pool de subidas con tope de 3 concurrentes. Evita saturar el
            // ancho de banda del usuario y competir con extract-invoice.
            const POOL = 3;
            let i = 0;
            const worker = async () => {
              while (i < pendingUploads.length) {
                const idx = i++;
                const f = pendingUploads[idx];
                try {
                  const path = await uploadFile(userId, f.id, f.file);
                  pathMapRef.current.set(f.id, path);
                  uploadedIdsRef.current.add(f.id);
                } catch (err) {
                  console.error("Failed to upload session file:", err);
                }
              }
            };
            await Promise.all(Array.from({ length: Math.min(POOL, pendingUploads.length) }, () => worker()));
          }

          // Now save metadata to DB
          const row = {
            user_id: userId,
            environment: "received",
            pending_files: serialisePending(s.pendingFiles, pathMapRef.current) as unknown as any,
            processed_files: serialiseProcessed(s.processed, pathMapRef.current) as unknown as any,
            failed_files: serialiseFailed(s.failedFiles, pathMapRef.current) as unknown as any,
            trashed_files: serialiseTrashed(s.trashedFiles, pathMapRef.current) as unknown as any,
            prefix: s.prefix,
            start_number: s.startNumber,
            applied_company_id: s.appliedCompanyId ?? null,
          };

          const { error } = await supabase.from("invoice_sessions").upsert(row, { onConflict: "user_id,environment" });

          if (error) throw error;
          setLastSavedAt(Date.now());
          setSaveStatus("saved");
        } catch (e) {
          console.error("Failed to save invoice session:", e);
          setSaveStatus("error");
        }
      }, 1500); // 1.5s debounce
    },
    [userId],
  );

  // ─── Clear session ─────────────────────────────────────

  const clearSession = useCallback(async () => {
    if (!userId) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    latestStateRef.current = null;

    try {
      await Promise.allSettled([
        supabase.from("invoice_sessions").delete().eq("user_id", userId).eq("environment", "received"),
        deleteFolder(userId),
      ]);
    } catch (e) {
      console.error("Failed to clear invoice session:", e);
    }

    pathMapRef.current.clear();
    uploadedIdsRef.current.clear();
  }, [userId]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return { loading, initialState, saveSession, clearSession, saveStatus, lastSavedAt };
}
